import { useCallback, useEffect, useState } from "react";
import api from "./api";

/**
 * useEntitlements — D7a (iter134).
 *
 * Fetches /api/entitlements once on mount and caches. All paywall +
 * blur decisions consult this. Server-authoritative — the hook
 * never guesses.
 *
 * Returns:
 *   {loading, tier, isElite, isAdmin,
 *    freeCredits: {doctorReport: {available, usedAt}},
 *    previewAsFreeUntil, refresh, togglePreviewAsFree}
 */
export function useEntitlements() {
  const [state, setState] = useState({
    loading: true,
    tier: "free",
    isElite: false,
    isAdmin: false,
    freeCredits: {doctorReport: {available: true, usedAt: null}},
    previewAsFreeUntil: null,
  });

  const refresh = useCallback(async () => {
    try {
      const {data} = await api.get("/entitlements");
      setState({
        loading: false,
        tier: data.tier,
        isElite: data.tier === "elite",
        isAdmin: !!data.is_admin,
        freeCredits: {
          doctorReport: {
            available: !!data.free_credits?.doctor_report?.available,
            usedAt: data.free_credits?.doctor_report?.used_at || null,
          },
        },
        previewAsFreeUntil: data.preview_as_free_until || null,
      });
    } catch {
      setState((s) => ({...s, loading: false}));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const togglePreviewAsFree = useCallback(async (enabled, durationMinutes = 60) => {
    try {
      await api.post("/admin/preview-tier/free", {
        enabled, duration_minutes: durationMinutes,
      });
      await refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  return {...state, refresh, togglePreviewAsFree};
}
